import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function resolveValue(data, key) {
  try {
    if (Array.isArray(key)) return key.reduce((acc, k) => acc[k], data);
    return data[key];
  } catch {
    return null;
  }
}

function resolveLink(template, data) {
  return template.replace(/:\w+/g, (match) => data[match.slice(1)] ?? match);
}

// ─── Row ─────────────────────────────────────────────────────────────────────

function TableRow({ data, columns, striped }) {
  const base = striped ? 'bg-gray-50' : 'bg-white';

  return (
    <div className={`flex w-full gap-1 rounded-lg overflow-hidden`}>
      {columns.map((col, i) => {
        const value = resolveValue(data, col.key);

        if (col.button?.link && !col.button?.callback) {
          const href = resolveLink(col.button.link, data);
          return (
            <Link
              key={i}
              to={href}
              target={col.button.external ? '_blank' : undefined}
              className={`shrink-0 flex items-center justify-center px-2 py-2 text-sm font-semibold text-red-1 hover:text-red-2 hover:underline transition-colors ${base}`}
              style={{ width: col.width || '80px', minWidth: col.width || '80px' }}
            >
              {col.button.textFn ? col.button.textFn(value) : col.button.text ?? 'Ver'}
            </Link>
          );
        }

        if (col.button?.callback) {
          return (
            <button
              key={i}
              onClick={() => col.button.callback(value, data)}
              className={`shrink-0 flex items-center justify-center px-2 py-2 text-sm font-semibold text-red-1 hover:text-red-2 hover:underline transition-colors ${base}`}
              style={{ width: col.width || '80px', minWidth: col.width || '80px' }}
            >
              {col.button.textFn ? col.button.textFn(value) : col.button.text ?? 'Acción'}
            </button>
          );
        }

        const display = col.mutator ? col.mutator(value, data) : (value ?? '—');

        return (
          <div
            key={i}
            className={`flex-1 min-w-0 px-3 py-2 text-sm text-gray-700 truncate ${base} ${col.className || ''}`}
            style={col.cellStyle}
            title={typeof display === 'string' ? display : undefined}
          >
            {display}
          </div>
        );
      })}
    </div>
  );
}

// ─── Sort icon ───────────────────────────────────────────────────────────────

function SortIcon({ dir }) {
  return (
    <span className='inline-flex flex-col ml-1 gap-px shrink-0'>
      <svg
        className={`w-2.5 h-2.5 transition-colors ${dir === 'asc' ? 'text-red-1' : 'text-gray-300'}`}
        viewBox='0 0 10 6' fill='currentColor'
      >
        <path d='M5 0L10 6H0L5 0Z' />
      </svg>
      <svg
        className={`w-2.5 h-2.5 transition-colors ${dir === 'desc' ? 'text-red-1' : 'text-gray-300'}`}
        viewBox='0 0 10 6' fill='currentColor'
      >
        <path d='M5 6L0 0H10L5 6Z' />
      </svg>
    </span>
  );
}

// ─── Header ──────────────────────────────────────────────────────────────────

function TableHeader({ columns, sortKey, sortDir, onSort }) {
  return (
    <div className='flex w-full gap-1 rounded-lg overflow-hidden bg-gray-100 mb-1'>
      {columns.map((col, i) => {
        const isButton = !!col.button;
        const isSortable = col.sortable && !isButton;
        const isActive = sortKey === col.key;

        return (
          <div
            key={i}
            onClick={isSortable ? () => onSort(col.key) : undefined}
            className={`px-3 py-2 text-xs font-bold text-darkred uppercase tracking-wide truncate flex items-center
              ${isSortable ? 'cursor-pointer select-none hover:bg-gray-200 transition-colors' : ''}`}
            style={
              isButton
                ? { width: col.width || '80px', minWidth: col.width || '80px' }
                : { flex: 1, minWidth: 0 }
            }
          >
            <span className='truncate flex-1'>{col.title ?? ''}</span>
            {isSortable && <SortIcon dir={isActive ? sortDir : null} />}
          </div>
        );
      })}
    </div>
  );
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function Skeleton({ rows }) {
  return (
    <div className='flex flex-col gap-1 mt-1'>
      {[...Array(rows)].map((_, i) => (
        <div
          key={i}
          className={`h-9 w-full rounded-lg ${i % 2 === 0 ? 'animate-pulse bg-gray-100' : 'animate-pulse bg-gray-50'}`}
          style={{ animationDelay: `${(i % 2) * 0.3}s` }}
        />
      ))}
    </div>
  );
}

// ─── Empty fillers ─────────────────────────────────────────────────────────────

function Fillers({ count, startIndex = 0 }) {
  if (count <= 0) return null;
  return (
    <>
      {[...Array(count)].map((_, i) => (
        <div
          key={i}
          className={`h-9 w-full rounded-lg ${(startIndex + i) % 2 === 0 ? 'bg-white' : 'bg-gray-50'}`}
        />
      ))}
    </>
  );
}

// ─── Footer / Pagination ─────────────────────────────────────────────────────

function TableFooter({ page, maxPage, onChangePage }) {
  return (
    <div className='flex items-center justify-between mt-1 bg-gray-100 rounded-lg px-3 py-1.5'>
      <span className='text-sm font-medium text-gray-600'>
        Página {page + 1} de {maxPage}
      </span>
      <div className='flex gap-1'>
        <button
          onClick={() => onChangePage(page - 1)}
          disabled={page === 0}
          className='flex items-center justify-center w-7 h-7 rounded bg-white text-gray-600 hover:text-red-1 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm'
        >
          <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M15 19l-7-7 7-7' />
          </svg>
        </button>
        <button
          onClick={() => onChangePage(page + 1)}
          disabled={page + 1 >= maxPage}
          className='flex items-center justify-center w-7 h-7 rounded bg-white text-gray-600 hover:text-red-1 disabled:text-gray-300 disabled:cursor-not-allowed transition-colors shadow-sm'
        >
          <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M9 5l7 7-7 7' />
          </svg>
        </button>
      </div>
    </div>
  );
}

// ─── Tools (search + create) ─────────────────────────────────────────────────

function TableTools({ tools, onSearch, onCreate, setPage }) {
  const [search, setSearch] = useState('');
  const lastSearch = useRef('');

  useEffect(() => {
    const trimmed = search.trim();
    if (trimmed === lastSearch.current) return;
    const timer = setTimeout(() => {
      lastSearch.current = trimmed;
      if (onSearch) onSearch(trimmed);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [search]);

  if (!tools?.length) return null;

  const has = (t) => tools.map((x) => x.toLowerCase()).includes(t);

  return (
    <div className='flex items-center gap-2 mb-2'>
      {has('search') && (
        <div className='flex items-center gap-2 flex-1 bg-white border border-gray-200 rounded-lg px-3 py-1.5 focus-within:border-red-1 transition-colors'>
          <svg className='w-4 h-4 text-gray-400 shrink-0' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M21 21l-4.35-4.35M17 11A6 6 0 1 1 5 11a6 6 0 0 1 12 0z' />
          </svg>
          <input
            type='text'
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder='Buscar...'
            className='flex-1 text-sm outline-none bg-transparent text-gray-700 placeholder-gray-400'
          />
          {search && (
            <button onClick={() => setSearch('')} className='text-gray-400 hover:text-gray-600 transition-colors'>
              <svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
                <path strokeLinecap='round' strokeLinejoin='round' d='M6 18L18 6M6 6l12 12' />
              </svg>
            </button>
          )}
        </div>
      )}
      {has('create') && onCreate && (
        <button
          onClick={onCreate}
          className='flex items-center gap-1.5 bg-red-1 hover:bg-red-2 text-white text-sm font-semibold px-3 py-1.5 rounded-lg transition-colors shrink-0'
        >
          <svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' strokeWidth={2.5}>
            <path strokeLinecap='round' strokeLinejoin='round' d='M12 4v16m8-8H4' />
          </svg>
          Crear
        </button>
      )}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────────────────

/**
 * Reusable data table with pagination, search, and flexible column definitions.
 *
 * Column shape:
 *   { key, title, mutator?, width?, className?, cellStyle?,
 *     button?: { link?, callback?, text?, textFn?, external? } }
 *
 * Column shape:
 *   { key, title, sortable?, mutator?, width?, className?, cellStyle?,
 *     button?: { link?, callback?, text?, textFn?, external? } }
 *
 * Props:
 *   data            – array of row objects
 *   columns         – column definitions (see above)
 *   loading         – show skeleton instead of rows
 *   elementsPerPage – rows per page (default 10)
 *   maxPage         – override max page (required for ajax mode)
 *   ajax            – if true, parent handles slicing; data should be current page only
 *   onChangePage    – called with new page index (0-based) when page changes
 *   onSort          – called with (key, dir) when sort changes; dir is 'asc'|'desc'|null
 *                     In ajax mode this is required to send sortBy to the backend.
 *                     In non-ajax mode the table sorts client-side automatically.
 *   tools           – array of tool names: ['search', 'create']
 *   onSearch        – called with search string when search changes
 *   onCreate        – called when Create button is clicked
 */
export default function Table({
  data = [],
  columns = [],
  loading = false,
  elementsPerPage = 10,
  maxPage: maxPageProp,
  ajax = false,
  onChangePage,
  onSort,
  tools,
  onSearch,
  onCreate,
}) {
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState(null); // 'asc' | 'desc' | null

  const maxPage = maxPageProp ?? (Math.ceil(data.length / elementsPerPage) || 1);

  const changePage = (newPage) => {
    if (newPage < 0 || newPage >= maxPage) return;
    setPage(newPage);
    if (onChangePage) onChangePage(newPage);
  };

  const handleSort = (key) => {
    let nextDir;
    if (sortKey !== key) {
      nextDir = 'asc';
    } else if (sortDir === 'asc') {
      nextDir = 'desc';
    } else {
      nextDir = null;
    }
    setSortKey(nextDir ? key : null);
    setSortDir(nextDir);
    setPage(0);
    if (onSort) onSort(nextDir ? key : null, nextDir);
  };

  // Client-side sort (non-ajax only)
  const sortedData = (!ajax && sortKey && sortDir)
    ? [...data].sort((a, b) => {
        const av = resolveValue(a, sortKey);
        const bv = resolveValue(b, sortKey);
        if (av == null) return 1;
        if (bv == null) return -1;
        const cmp = typeof av === 'string' ? av.localeCompare(bv) : av - bv;
        return sortDir === 'asc' ? cmp : -cmp;
      })
    : data;

  const slice = ajax
    ? sortedData.slice(0, elementsPerPage)
    : sortedData.slice(page * elementsPerPage, page * elementsPerPage + elementsPerPage);

  return (
    <div className='w-full font-montserrat flex flex-col gap-1'>
      <TableTools tools={tools} onSearch={onSearch} onCreate={onCreate} setPage={setPage} />
      <TableHeader columns={columns} sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
      {loading ? (
        <Skeleton rows={elementsPerPage} />
      ) : (
        <div className='flex flex-col gap-1'>
          {slice.map((row, i) => (
            <TableRow key={i} data={row} columns={columns} striped={i % 2 !== 0} />
          ))}
          <Fillers count={elementsPerPage - slice.length} startIndex={slice.length} />
        </div>
      )}
      <TableFooter page={page} maxPage={maxPage} onChangePage={changePage} />
    </div>
  );
}
